import type { ShowNotification, ShowNotificationOptions } from "@toolpad/core";
import { CombinedGraphQLErrors } from "@apollo/client/errors";
import { Typography } from "@trimble/modus-cmx";
import type { ReactNode } from "react";
import i18n from "@/config/i18n";
import { isForbiddenApiError } from "@/utils/forbiddenApiError";
import type { GraphQLError } from "./graphqlError";

export type ErrorToastNotifications = {
  show: ShowNotification;
};

export type ShowErrorToastOptions = Pick<
  ShowNotificationOptions,
  "autoHideDuration" | "actionText" | "onAction" | "key"
>;

export function extractGraphQLExtensions(error: unknown): {
  message?: string;
  code?: string;
  traceId?: string;
} {
  if (!CombinedGraphQLErrors.is(error)) return {};
  const graphQLError: GraphQLError | undefined = error.errors[0];
  const ext = graphQLError?.extensions;
  if (!ext) return {};

  const exceptionMessage = ext.exception?.message;

  return {
    message:
      typeof exceptionMessage === "string" ? exceptionMessage : undefined,
    code: typeof ext.code === "string" ? ext.code : undefined,
    traceId: typeof ext.traceId === "string" ? ext.traceId : undefined,
  };
}

export const extractReadableErrorCode = (error: unknown): string | null => {
  if (typeof error !== "object" || error === null || !("errors" in error)) {
    return null;
  }

  const errors: unknown = error.errors;
  if (!Array.isArray(errors)) {
    return null;
  }

  const firstError: unknown = errors[0];
  if (
    typeof firstError !== "object" ||
    firstError === null ||
    !("extensions" in firstError)
  ) {
    return null;
  }

  const extensions = firstError.extensions;
  if (
    typeof extensions !== "object" ||
    extensions === null ||
    !("readableErrorCode" in extensions)
  ) {
    return null;
  }

  const { readableErrorCode } = extensions;
  return typeof readableErrorCode === "string" ? readableErrorCode : null;
};

/**
 * Shows a local error toast for Apollo/network failures, unless the error is
 * forbidden. Forbidden errors are handled by the global Apollo ErrorLink.
 *
 * Appends extensions.message and extensions.code from the GraphQL error when
 * present, each on its own line. Toasts do not auto-hide; the user must
 * dismiss them manually.
 *
 * @returns `false` when suppressed (forbidden); otherwise the notification key.
 */
export function showErrorToast(
  notifications: ErrorToastNotifications,
  error: unknown,
  message: ReactNode,
  options?: ShowErrorToastOptions
): false | string {
  if (isForbiddenApiError(error)) {
    return false;
  }

  const {
    message: extMessage,
    code: extCode,
    traceId: extTraceId,
  } = extractGraphQLExtensions(error);

  const finalMessage =
    (extMessage ?? extCode) ? (
      <>
        <Typography hierarchy="p" size="md">
          {message}
        </Typography>

        {extCode && (
          <Typography
            hierarchy="p"
            size="sm"
            style={{
              whiteSpace: "normal",
              overflowWrap: "break-word",
              textWrap: "pretty",
            }}
          >
            <span>
              <strong>{i18n.t("error.error_code_label")}</strong>
              {` ${extCode}`}
            </span>
          </Typography>
        )}
        {extMessage && (
          <Typography
            hierarchy="p"
            size="sm"
            style={{
              whiteSpace: "normal",
              overflowWrap: "break-word",
              textWrap: "pretty",
            }}
          >
            <span>
              <strong>{i18n.t("error.error_reason_label")}</strong>
              {` ${extMessage}`}
            </span>
          </Typography>
        )}
        {extTraceId && (
          <Typography
            hierarchy="p"
            size="sm"
            style={{
              whiteSpace: "normal",
              overflowWrap: "break-word",
              textWrap: "pretty",
            }}
          >
            <span>
              <strong>{i18n.t("error.trace_id_label")}</strong>
              {` ${extTraceId}`}
            </span>
          </Typography>
        )}
      </>
    ) : (
      message
    );

  return notifications.show(finalMessage, {
    ...options,
    severity: "error",
  });
}
